import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import SafeImage from "../../../components/SafeImage";

/**
 * SRC-styled product card. Same aspect ratio (3/4), same hover swap for
 * secondary image, same badges (New, Bestseller, Limited, Sale%), same
 * "Only N left" low-stock indicator. Accent color read from context
 * `config.brand.accentColor` so different store types can override it.
 */
export default function ProductCard({ product, basePath = "", accent = "#E60000", eager = false }) {
  const onSale = !!product.compare_at_price && product.compare_at_price > product.price;
  const discount = onSale
    ? Math.round(100 - (product.price / product.compare_at_price) * 100)
    : 0;
  const stock = typeof product.stock === "number" ? product.stock : 99;
  const isLowStock = stock > 0 && stock <= 8;
  const rating = typeof product.rating === "number" ? product.rating : null;
  const badges = product.badges || [];

  return (
    <Link
      to={`${basePath}/product/${product.slug || product.id}`}
      data-testid={`product-card-${product.slug || product.id}`}
      className="group block"
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-neutral-100">
        <SafeImage
          src={product.images?.[0] || product.image_url}
          alt={product.name}
          loading={eager ? "eager" : "lazy"}
          className="absolute inset-0 w-full h-full transition-transform duration-700 group-hover:scale-105"
          style={{ objectFit: "cover" }}
          fallbackLabel={product.name}
        />
        {product.images?.[1] && (
          <img
            src={product.images[1]}
            alt=""
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          />
        )}

        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {badges.includes("New") && (
            <span className="bg-white text-black text-[10px] font-bold uppercase tracking-widest px-2 py-1">New</span>
          )}
          {badges.includes("Bestseller") && (
            <span className="bg-black text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">Bestseller</span>
          )}
          {badges.includes("Limited") && (
            <span className="text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1" style={{ background: accent }}>Limited</span>
          )}
          {onSale && (
            <span className="text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1" style={{ background: accent }}>
              -{discount}%
            </span>
          )}
        </div>

        {isLowStock && (
          <div className="absolute bottom-3 left-3 right-3">
            <span data-testid={`low-stock-${product.slug || product.id}`} className="inline-block bg-black/90 text-white text-[10px] font-medium uppercase tracking-widest px-2 py-1">
              Only {stock} left
            </span>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-heading font-semibold text-sm leading-tight truncate">{product.name}</p>
          {product.category && (
            <p className="text-xs text-neutral-500 mt-0.5 truncate">{product.category}</p>
          )}
          {rating !== null && (
            <div className="flex items-center gap-1 mt-1.5 text-[11px] text-neutral-500">
              <Star className="w-3 h-3 fill-current text-amber-500" strokeWidth={1.5} />
              {rating.toFixed(1)}
              {product.review_count ? ` (${product.review_count})` : ""}
            </div>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-heading text-sm">${Number(product.price).toFixed(2)}</p>
          {onSale && (
            <p className="text-xs text-neutral-400 line-through mt-0.5">${Number(product.compare_at_price).toFixed(2)}</p>
          )}
        </div>
      </div>
    </Link>
  );
}
